// Single source of truth for every Signal Analysis calculation.
//
// These are pure functions extracted from the SignalAnalysis component so they can
// be unit-tested (see signalAnalysisCalcs.test.js). The page must not re-implement
// any of this inline — history shows inline calc code breeds bugs (the additive
// strategy return made a winning strategy look like it lost to buy-and-hold).
//
// Core model: equal capital allocated at window start across every index that has a
// counted trade; each bucket runs independently (no cross-index rebalancing); the
// headline is the equal-weight average of bucket returns, with BOTH sides geometric:
//   INDEX bucket    = last close / first close − 1        (buy & hold)
//   STRATEGY bucket = ∏(1 + gain) − 1                      (capital compounds per trade)

const round2 = (x) => Math.round(x * 100) / 100;

// Two questions, two filters (Apal 2026-10-16: "if i select what trades came
// after 1st oct … it's not a new open trade … make sure all calculations of
// values are not messed up"):
//
//   WHAT THE MONEY DID — Strategy %, % in market, the headline vs the index,
//   the Brief cards, the allocation. Every position HELD in the period counts,
//   including one carried in from before it (the backend values it from the
//   last close before the period — Compare's rule, Apal 2026-10-11).
//
//   TRADES OF THE PERIOD — W/L, win rate, avg win/loss, best/worst, streaks,
//   avg days, MAE. Only trades ENTERED in the period. A carried-in position
//   is not a trade of this period: its slice is a mark-to-market move, not a
//   trade outcome (the FTSE long held since 17-04-2025 read as a new 1–9 Oct
//   "losing trade").
//
// OPEN AT THE END means still held on the period's last day — today's open
// trade, or a position the period cut at its last close (backend
// carried_out). That is how the trade list SHOWS a row. The Open trades
// switch keeps its meaning — trades open TODAY — so no past period's return
// moves with it (checked on the real data: 6M, 1Y, 1–9 Oct, 2022, every mode,
// both switch states — 0 per-index returns change; Compare stays matched).

// Held on the period's last day (display).
export const isOpenAtEnd = (t) => Boolean(t.is_open || t.carried_out);

// Entered before the period. The backend marks clipped trades (carried_in,
// with the real entry in orig_entry_date); an unclipped trade is judged by its
// own entry date. Boundary: entering ON the first day is in-period.
export const isCarriedIn = (t, periodFrom) => Boolean(t.carried_in)
  || Boolean(periodFrom && (t.orig_entry_date || t.entry_date || '') < periodFrom);

// Open today and already open before the period.
export const isPrePeriodOpen = (t, periodFrom) => Boolean(t.is_open) && isCarriedIn(t, periodFrom);

// TRADES OF THE PERIOD — the trade-statistics rule.
export const makeTradeFilter = ({ includeOpen, periodFrom }) => (t) =>
  !isCarriedIn(t, periodFrom) && (includeOpen || !t.is_open);

// WHAT THE MONEY DID — the returns / exposure rule.
export const makeHeldFilter = ({ includeOpen }) => (t) => includeOpen || !t.is_open;

// Compounded strategy return for one index's trades: ∏(1+gain) − 1, the same
// geometric form as the buy-&-hold INDEX side. A plain sum of per-trade %s
// understates long winning sequences badly (30 × +5% sums to +150% but compounds
// to +332%) — summing is how the strategy falsely "lost" to buy-and-hold on
// multi-year windows. Matches the Cumulative % method in the transaction reports.
export const compoundGain = (trades) => trades.length
  ? Math.round((trades.reduce((m, t) => m * (1 + t.gain / 100), 1) - 1) * 10000) / 100
  : 0;

// Buy-&-hold index return over the price window, or null with fewer than 2 rows.
// Index return over a window, from the close BEFORE it (`basis`) when known —
// the basis carried positions use, so index and strategy cover the same
// sessions. Without one (data starts inside the window) it starts at the first.
export const indexReturnPct = (prices, basis = null) => {
  if (!prices || !prices.length) return null;
  const start = basis && basis.close > 0 ? basis.close : (prices.length >= 2 ? prices[0].close : null);
  return start ? Math.round((prices[prices.length - 1].close / start - 1) * 10000) / 100 : null;
};

const avgDaysOf = (arr) => {
  const d = arr.map(t => t.days_held).filter(x => x != null);
  return d.length ? Math.round(d.reduce((s, v) => s + v, 0) / d.length) : null;
};

// Per-index card stats over the counted trades (`comp` = gain != null && included).
// Open trades counted here are valued at the current price, so an open trade in
// profit is a win and one under water is a loss — same rule as closed trades.
export const computeSymbolStats = (comp) => {
  const w = comp.filter(t => t.gain > 0);
  const l = comp.filter(t => t.gain <= 0);
  const maeV = comp.map(t => t.mae).filter(m => m != null);
  return {
    strat_return: compoundGain(comp),
    gross_gains: w.length ? round2(w.reduce((s, t) => s + t.gain, 0)) : 0,
    gross_losses: l.length ? round2(l.reduce((s, t) => s + t.gain, 0)) : 0,
    winning: w.length, losing: l.length,
    win_rate: comp.length ? Math.round(w.length / comp.length * 1000) / 10 : 0,
    avg_win: w.length ? round2(w.reduce((s, t) => s + t.gain, 0) / w.length) : 0,
    avg_loss: l.length ? round2(l.reduce((s, t) => s + t.gain, 0) / l.length) : 0,
    best_trade: comp.length ? round2(Math.max(...comp.map(t => t.gain))) : null,
    worst_trade: comp.length ? round2(Math.min(...comp.map(t => t.gain))) : null,
    avg_mae: maeV.length ? round2(maeV.reduce((s, v) => s + v, 0) / maeV.length) : null,
    avg_days: avgDaysOf(comp),
    avg_days_long: avgDaysOf(comp.filter(t => t.direction === 'LONG')),
    avg_days_short: avgDaysOf(comp.filter(t => t.direction === 'SHORT')),
  };
};

const EMPTY_SUMMARY = {
  total_trades: 0, winning_trades: 0, losing_trades: 0, win_rate: 0,
  gross_gains: 0, gross_losses: 0, avg_win: 0, avg_loss: 0,
  best_trade: null, worst_trade: null,
  max_cons_wins: 0, max_cons_wins_gain: 0, max_cons_losses: 0, max_cons_losses_loss: 0,
  avg_mae: null, max_mae: null, avg_days: null, avg_days_long: null, avg_days_short: null,
};

// Overall summary across all regions/symbols, over the included trades only.
export const computeOverallSummary = (regions, tradeFilter) => {
  if (!regions) return null;
  const all = [];
  regions.forEach(r => r.symbols.forEach(s => s.trades.forEach(t => {
    if (tradeFilter(t)) all.push(t);
  })));
  const completed = all.filter(t => t.gain != null);
  if (!completed.length) return { ...EMPTY_SUMMARY };
  const wins = completed.filter(t => t.gain > 0);
  const losses = completed.filter(t => t.gain <= 0);
  const mae_vals = completed.map(t => t.mae).filter(m => m != null);
  const best = completed.reduce((a, b) => a.gain > b.gain ? a : b);
  const worst = completed.reduce((a, b) => a.gain < b.gain ? a : b);
  // Consecutive streaks in exit-date order (entry date stands in for open trades)
  const sorted = [...completed].sort((a, b) => (a.exit_date || a.entry_date || '').localeCompare(b.exit_date || b.entry_date || ''));
  let mcw = 0, mcwg = 0, mcl = 0, mcll = 0, cw = 0, cwg = 0, cl = 0, cll = 0;
  for (const t of sorted) {
    if (t.gain > 0) { cw++; cwg += t.gain; if (cw > mcw) { mcw = cw; mcwg = round2(cwg); } cl = 0; cll = 0; }
    else { cl++; cll += t.gain; if (cl > mcl) { mcl = cl; mcll = round2(cll); } cw = 0; cwg = 0; }
  }
  return {
    total_trades: completed.length, winning_trades: wins.length, losing_trades: losses.length,
    win_rate: Math.round(wins.length / completed.length * 1000) / 10,
    gross_gains: round2(wins.reduce((s, t) => s + t.gain, 0)),
    gross_losses: round2(losses.reduce((s, t) => s + t.gain, 0)),
    avg_win: wins.length ? round2(wins.reduce((s, t) => s + t.gain, 0) / wins.length) : 0,
    avg_loss: losses.length ? round2(losses.reduce((s, t) => s + t.gain, 0) / losses.length) : 0,
    best_trade: { gain: round2(best.gain), symbol: best.symbol_label || best.symbol || '', exit_date: best.exit_date || best.entry_date },
    worst_trade: { gain: round2(worst.gain), symbol: worst.symbol_label || worst.symbol || '', exit_date: worst.exit_date || worst.entry_date },
    max_cons_wins: mcw, max_cons_wins_gain: mcwg, max_cons_losses: mcl, max_cons_losses_loss: mcll,
    avg_mae: mae_vals.length ? round2(mae_vals.reduce((s, v) => s + v, 0) / mae_vals.length) : null,
    max_mae: mae_vals.length ? round2(Math.max(...mae_vals)) : null,
    avg_days: avgDaysOf(completed),
    avg_days_long: avgDaysOf(completed.filter(t => t.direction === 'LONG')),
    avg_days_short: avgDaysOf(completed.filter(t => t.direction === 'SHORT')),
  };
};

// Indexes dropped from the analysis — no position counted in the current view
// (pass the HELD filter), with WHY:
//   open_only — only positions open today, Open trades off
//   no_trades — genuinely no position in the window
// (A position carried in from before the period counts — Compare's rule — so
// it never drops an index; the trade list shows it as carried in.)
export const computeExcludedSymbols = (regions, heldFilter, { includeOpen }) => {
  if (!regions) return [];
  const out = [];
  regions.forEach(r => r.symbols.forEach(s => {
    const trades = s.trades || [];
    const counted = trades.filter(t => t.gain != null && heldFilter(t));
    if (counted.length) return;
    const reason = !includeOpen && trades.some(t => t.is_open) ? 'open_only' : 'no_trades';
    out.push({ label: s.label || s.symbol, reason });
  }));
  return out;
};

// Headline INDEX vs STRATEGY: equal-weight average of per-index buckets. An index
// is in the comparison ONLY if it has >=2 price rows AND at least one counted trade —
// and then it is in BOTH sides, so exclusions can never skew one side.
// Invariant this guarantees: if the strategy beats buy-&-hold in every counted
// index, the headline strategy beats the headline index.
/**
 * Headline Index vs Strategy: equal weight across every selected index that
 * was AVAILABLE in the period — it has prices in the window (Apal 2026-10-11:
 * "if equal weight then are we adjusting weights based on availability" — and
 * "if the selection was same on one page and other page is similar selection
 * then output gotta be same"). This is Compare Indexes' rule:
 *   - an index with no data in the period (Nifty in 2005, Bitcoin before 2017)
 *     takes no share — the money is split across the ones that existed;
 *   - an index that existed but where the strategy made no trade sat in CASH:
 *     strategy 0%, its own buy & hold on the index side (dropping it would
 *     quietly hand its share to the indexes that did trade — hindsight);
 *   - an index whose only trades are excluded by the view (Include Open off)
 *     drops from both sides, as before.
 * Trades arrive already clipped to the period by the backend (positions held
 * into it start at the last close before it; held past it, close at its last
 * close), so a calendar year here IS Compare's yearly number.
 * Returns { indexRet, stratRet, count, members: [{symbol, label, weight, stratRet, indexRet}] }.
 */
const computeOnePeriod = (priceChart, regions, tradeFilter, onlySymbols = null) => {
  if (!priceChart || !regions) return null;
  // only what's selected — a stale or wider price response never joins the split
  const syms = Object.keys(priceChart).filter(sym => !onlySymbols || onlySymbols.includes(sym));
  if (!syms.length) return null;
  const bySym = {};
  regions.forEach(r => r.symbols.forEach(s => { bySym[s.symbol] = s; }));
  const members = [];
  syms.forEach(sym => {
    const prices = priceChart[sym]?.prices;
    if (!prices || prices.length < 2) return;                 // not available in the period
    const s = bySym[sym];
    const all = (s?.trades || []).filter(t => t.gain != null);
    const counted = all.filter(t => tradeFilter(t));
    if (all.length && !counted.length) return;                // only view-excluded trades (e.g. open, Include Open off)
    // No trades this period → 0% allocation, off BOTH sides. The reports are
    // the truth (Apal 2026-10-11): a year has a row only when the index held a
    // position in it, and Compare spreads a missing year's share over the
    // rest. Before its first signal (India before 2006) the same — otherwise
    // its buy & hold leaks into the index side (2005 showed +27%).
    if (!all.length) return;
    members.push({
      symbol: sym,
      label: s?.label || priceChart[sym]?.label || sym,
      stratRet: compoundGain(counted),
      indexRet: (prices[prices.length - 1].close / prices[0].close - 1) * 100,
    });
  });
  if (!members.length) return null;
  const n = members.length;
  const out = members.map(m => ({ ...m, weight: round2(100 / n), stratRet: round2(m.stratRet), indexRet: round2(m.indexRet) }));
  return {
    indexRet: round2(members.reduce((x, m) => x + m.indexRet, 0) / n),
    stratRet: round2(members.reduce((x, m) => x + m.stratRet, 0) / n),
    count: n,
    members: out,
  };
};

// A trade cut to [from, to] — the yearly rule (claritybox_reports
// yearly_stats_generator): held into the slice, it starts at the close of the
// last trading day before it; held past it, it ends at the slice's last close.
const sliceTrade = (t, from, to, prices) => {
  const entry = t.entry_date, exit = t.exit_date || prices[prices.length - 1]?.date;
  if (!entry || entry > to || (exit && exit < from)) return null;
  const carriedIn = entry < from, carriedOut = exit > to;
  if (!carriedIn && !carriedOut) return t;
  const before = prices.filter(p => p.date < from);
  const inside = prices.filter(p => p.date >= from && p.date <= to);
  if (!inside.length) return null;
  const basis = carriedIn ? before[before.length - 1]?.close : t.entry_price;
  const end = carriedOut ? inside[inside.length - 1].close : t.exit_price;
  if (!basis || end == null) return null;
  const gain = t.direction === 'SHORT' ? (basis - end) / basis * 100 : (end - basis) / basis * 100;
  return {
    ...t, gain,
    entry_date: carriedIn ? inside[0].date : entry, entry_price: basis,
    exit_date: carriedOut ? inside[inside.length - 1].date : t.exit_date, exit_price: end,
    is_open: !!t.is_open && !carriedOut,
  };
};

/**
 * Headline Index vs Strategy for the period (Apal 2026-10-11: "if there are
 * multiple years or ranges that has some available some not, we still are
 * able to show clearly"). Within one calendar year it is one equal-weight
 * split over the indexes that held a position. A range across years is cut at
 * each 1 Jan, every year split over the indexes available THAT year, and the
 * years compounded — exactly how Compare Indexes builds a multi-year horizon,
 * so the same selection gives the same number on both pages.
 * Returns { indexRet, stratRet, count, members, segments: [{from, to, members}] }
 * where members is the latest year's split.
 */
export const computeSystemReturns = (priceChart, regions, tradeFilter, onlySymbols = null, signalStart = null, periodTo = null, periodFrom = null) => {
  if (!priceChart || !regions) return null;
  const single = () => {
    const r = computeOnePeriod(priceChart, regions, tradeFilter, onlySymbols);
    return r && { ...r, segments: [{ from: periodFrom, to: periodTo, members: r.members }] };
  };
  if (!periodFrom || !periodTo || periodFrom.slice(0, 4) === periodTo.slice(0, 4)) return single();
  const segments = [];
  for (let y = Number(periodFrom.slice(0, 4)); y <= Number(periodTo.slice(0, 4)); y++) {
    const from = y === Number(periodFrom.slice(0, 4)) ? periodFrom : `${y}-01-01`;
    const to = y === Number(periodTo.slice(0, 4)) ? periodTo : `${y}-12-31`;
    const segPrices = Object.fromEntries(Object.entries(priceChart).map(([sym, v]) =>
      [sym, { ...v, prices: (v?.prices || []).filter(p => p.date >= from && p.date <= to) }]));
    const segRegions = regions.map(r => ({ ...r, symbols: r.symbols.map(sy => ({
      ...sy,
      trades: (sy.trades || []).filter(t => t.gain != null)
        .map(t => sliceTrade(t, from, to, priceChart[sy.symbol]?.prices || [])).filter(Boolean),
    })) }));
    const r = computeOnePeriod(segPrices, segRegions, tradeFilter, onlySymbols);
    if (r) segments.push({ from, to, ...r });
  }
  if (!segments.length) return null;
  const comp = (k) => round2((segments.reduce((m, sg) => m * (1 + sg[k] / 100), 1) - 1) * 100);
  const latest = segments[segments.length - 1];
  return {
    indexRet: comp('indexRet'),
    stratRet: comp('stratRet'),
    count: latest.count,
    members: latest.members,
    segments: segments.map(sg => ({ from: sg.from, to: sg.to, members: sg.members })),
  };
};

// % of the selected window spent in the market, for one index's counted trades.
// Each trade occupies [entry_date, exit_date] (open trades run to the window end),
// clipped to the window; overlapping long/short intervals are UNIONED so a day
// never counts twice. Returns null when the window is unknown/empty.
export const computeInMarketPct = (trades, periodFrom, periodTo) => {
  if (!periodFrom || !periodTo) return null;
  const start = Date.parse(periodFrom);
  const end = Date.parse(periodTo);
  const totalMs = end - start;
  if (!(totalMs > 0)) return null;
  const intervals = trades
    .map(t => [
      Math.max(Date.parse(t.entry_date), start),
      Math.min(Date.parse(t.exit_date || t.last_close_date || periodTo), end),
    ])
    .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && b > a)
    .sort((a, b) => a[0] - b[0]);
  let inMs = 0, curS = null, curE = null;
  for (const [a, b] of intervals) {
    if (curS === null) { curS = a; curE = b; continue; }
    if (a <= curE) curE = Math.max(curE, b);
    else { inMs += curE - curS; curS = a; curE = b; }
  }
  if (curS !== null) inMs += curE - curS;
  return Math.min(100, Math.round((inMs / totalMs) * 1000) / 10);
};

// Per-symbol % in market plus the equal-weight overall (mean across indexes with a
// counted trade — the same equal-allocation model as computeSystemReturns, so the
// overall reads as the portfolio's average exposure).
export const computeInMarketBySymbol = (regions, tradeFilter, periodFrom, periodTo) => {
  const perSymbol = {};
  const vals = [];
  if (regions) {
    regions.forEach(r => r.symbols.forEach(s => {
      const counted = (s.trades || []).filter(t => t.gain != null && tradeFilter(t));
      if (!counted.length) return;
      const pct = computeInMarketPct(counted, periodFrom, periodTo);
      if (pct != null) { perSymbol[s.symbol] = pct; vals.push(pct); }
    }));
  }
  return {
    perSymbol,
    overall: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10 : null,
  };
};

/**
 * Signal Analysis — Brief (Apal 2026-10-08): "per market grouping but done in
 * a way that I can see what worked in each market and what didn't", bucketed
 * by OUR return against a bar that follows the period — 10% a year,
 * compounded (≥ 1 year: 1.10^(days/365) − 1 → 1Y 10%, 2Y 21%, 3Y 33%),
 * prorated below a year (10% × days/365 → 3M 2.5%, 6M 5%, 9M 7.5%); YTD and
 * custom use the range's actual days, a whole calendar year (1 Jan – 31 Dec,
 * the Year picker) counting as exactly 1 year:
 *   big   = ≥ bar
 *   small = 0 … < bar (flat 0% sits here: nothing was lost)
 *   lost  = < 0
 * Cards are placed against the bar AS SHOWN in the column head (whole % from
 * 10% up, one decimal below), so a card never sits under a head it contradicts.
 * The index's own return rides on every card, and each market says how many
 * of its indexes beat the index.
 *
 * One entry per index the headline counts (counted trades AND a price window —
 * the same set as computeSystemReturns), with the Detailed cards' own numbers
 * (computeSymbolStats, indexReturnPct). Each cell runs best return first;
 * markets run best average return first ("what worked the best for us"), ties
 * in `regions` order.
 */
export const BRIEF_BIG_PCT = 10; // a year, compounded
export const BRIEF_TIERS = ['big', 'small', 'lost'];
export const briefTier = (ours, bar = BRIEF_BIG_PCT) => (ours < 0 ? 'lost' : ours >= bar ? 'big' : 'small');

const fmtNum = (v) => String(Number(v.toFixed(1)));   // 2.5 → "2.5", 5.0 → "5", 1.37 → "1.4"

/** The period's "good" bar: { pct (as shown), label "21%", span "2 years", days }. */
export const briefBar = (from, to) => {
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 864e5);
  if (!(days > 0)) return { pct: BRIEF_BIG_PCT, label: `${BRIEF_BIG_PCT}%`, span: '1 year', days: 365 };
  // A whole calendar span reads as whole years: 1 Jan – 31 Dec is 364 days
  // between the dates and a leap year adds one — both are "1 year", not
  // "12 months prorated" (within ±4 days of a whole number of years).
  const exact = days / 365;
  const years = Math.round(exact) >= 1 && Math.abs(exact - Math.round(exact)) * 365 <= 4 ? Math.round(exact) : exact;
  const raw = years >= 1 ? (Math.pow(1 + BRIEF_BIG_PCT / 100, years) - 1) * 100 : BRIEF_BIG_PCT * years;
  const pct = raw >= 10 ? Math.round(raw) : Number(raw.toFixed(1));
  const months = Math.round(days / (365 / 12));
  const span = years >= 1 ? `${fmtNum(years)} ${fmtNum(years) === '1' ? 'year' : 'years'}`
    : months >= 1 ? `${months} ${months === 1 ? 'month' : 'months'}`
      : `${days} ${days === 1 ? 'day' : 'days'}`;
  return { pct, label: `${fmtNum(pct)}%`, span, days };
};

export const computeBriefView = (regions, priceChart, tradeFilter, period) => {
  const bar = briefBar(period?.from, period?.to);
  const markets = [];
  const counts = { big: 0, small: 0, lost: 0 };
  (regions || []).forEach(r => {
    const cells = { big: [], small: [], lost: [] };
    const all = [];
    (r.symbols || []).forEach(s => {
      const comp = (s.trades || []).filter(t => t.gain != null && tradeFilter(t));
      if (!comp.length) return;
      const index = indexReturnPct(priceChart?.[s.symbol]?.prices, priceChart?.[s.symbol]?.basis);
      if (index == null) return;
      const ours = computeSymbolStats(comp).strat_return;
      const edge = round2(ours - index);
      const tier = briefTier(ours, bar.pct);
      const card = { symbol: s.symbol, label: s.label || s.symbol, ours, index, edge, tier };
      cells[tier].push(card);
      all.push(card);
      counts[tier] += 1;
    });
    if (!all.length) return;
    BRIEF_TIERS.forEach(k => cells[k].sort((x, y) => (y.ours - x.ours) || x.label.localeCompare(y.label)));
    const avg = (k) => round2(all.reduce((sum, c) => sum + c[k], 0) / all.length);
    markets.push({
      market: (r.code || r.region || '').toUpperCase(), name: r.region, cells, count: all.length,
      ours: avg('ours'), index: avg('index'), beat: all.filter(c => c.edge >= 0).length,
    });
  });
  markets.sort((a, b) => b.ours - a.ours); // stable: ties keep `regions` order
  return { markets, counts, total: counts.big + counts.small + counts.lost, bar };
};

/**
 * "We try to win big and lose small": the period's average and best win
 * against its average and worst loss, from computeOverallSummary — the same
 * numbers as the Detailed summary cards. ratio = how many times bigger the
 * average win is than the average loss (null when nothing lost). The best and
 * worst trade are named from `regions` (the summary's trades carry no index).
 */
export const promiseCheck = (summary, regions, tradeFilter) => {
  if (!summary || !summary.total_trades) return null;
  const ratio = summary.avg_loss < 0 ? round2(summary.avg_win / Math.abs(summary.avg_loss)) : null;
  let best = null;
  let worst = null;
  (regions || []).forEach(r => (r.symbols || []).forEach(s => (s.trades || []).forEach(t => {
    if (t.gain == null || !tradeFilter || !tradeFilter(t)) return;
    if (!best || t.gain > best.gain) best = { gain: t.gain, label: s.label || s.symbol };
    if (!worst || t.gain < worst.gain) worst = { gain: t.gain, label: s.label || s.symbol };
  })));
  return {
    avgWin: summary.winning_trades ? summary.avg_win : null,
    bestWin: summary.winning_trades ? summary.best_trade?.gain ?? null : null,
    bestWinLabel: summary.winning_trades && best ? best.label : '',
    avgLoss: summary.losing_trades ? summary.avg_loss : null,
    worstLoss: summary.losing_trades ? summary.worst_trade?.gain ?? null : null,
    worstLossLabel: summary.losing_trades && worst ? worst.label : '',
    wins: summary.winning_trades,
    losses: summary.losing_trades,
    ratio,
    held: ratio == null ? true : ratio >= 1,
  };
};
